// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
	QrCodeLayout,
	QrCodeTracking,
	QrViewCloseReason,
	SlideUpQrCode,
} from '#src/app/pages/psf/promoteOrShare/shared/hooks';

import { CHANNEL_GRID_TRACKING_DATA } from './constants';

/** `grid` is the production share grid; `qr` is psf_promote_or_share_qr_code_experiment's view. */
type SlideUpStep = 'grid' | 'qr';

/**
 * Resets to the grid on every open. The reset runs during render, not in an effect, so an open never
 * flashes the last view and a close keeps the view through the exit animation:
 * https://react.dev/learn/you-might-not-need-an-effect
 */
function useSlideUpStep(isOpen: boolean) {
	const [step, setStep] = useState<SlideUpStep>('grid');
	const [wasOpen, setWasOpen] = useState(isOpen);
	if (wasOpen !== isOpen) {
		setWasOpen(isOpen);
		if (isOpen) setStep('grid');
	}
	return { step, setStep };
}

/**
 * EXPERIMENT: psf_promote_or_share_qr_code_experiment.
 * `qrView` is set only while the QR view shows. `openQrView` also fires the tile's intent event.
 *
 * Leaving the QR view records why, and `getExitReason` reads it when the view unmounts, so its close
 * event names the exit. A slide-up close leaves no reason here: the view sees `isOpen` turn false.
 */
export function useQrCodeSteps(
	isOpen: boolean,
	qrCodeLayout: QrCodeLayout | null | undefined,
	qrCode: SlideUpQrCode | undefined,
	qrCodeTracking: QrCodeTracking | undefined,
) {
	const { step, setStep } = useSlideUpStep(isOpen);
	const exitReasonRef = useRef<QrViewCloseReason | null>(null);
	const qrTileRef = useRef<HTMLDivElement>(null);
	const shouldFocusQrTileRef = useRef(false);

	/** Each QR view starts with no exit reason. Back from the poster view comes through here too. */
	const showQrView = useCallback(() => {
		exitReasonRef.current = null;
		setStep('qr');
	}, [setStep]);
	const openQrView = useCallback(() => {
		qrCodeTracking?.trackTileTapped(CHANNEL_GRID_TRACKING_DATA);
		showQrView();
	}, [qrCodeTracking, showQrView]);
	const leaveQrView = useCallback(
		(nextStep: SlideUpStep, reason: QrViewCloseReason) => {
			exitReasonRef.current = reason;
			setStep(nextStep);
		},
		[setStep],
	);
	const showGrid = useCallback(() => {
		shouldFocusQrTileRef.current = true;
		leaveQrView('grid', 'back');
	}, [leaveQrView]);

	// The back button unmounts with the QR view, so focus returns to the tile.
	useEffect(() => {
		if (step !== 'grid' || !shouldFocusQrTileRef.current) return;
		shouldFocusQrTileRef.current = false;
		qrTileRef.current?.querySelector('button')?.focus();
	}, [step]);
	// No poster view yet, so Print poster is a no-op.
	const openPosterView = useCallback(() => {}, []);
	const getExitReason = useCallback(() => exitReasonRef.current, []);

	const qrView =
		step === 'qr' && qrCodeLayout && qrCode && qrCodeTracking
			? { qrCode, showPrint: qrCodeLayout.showPrint, tracking: qrCodeTracking }
			: null;

	return {
		showQrTile: Boolean(qrCodeLayout && qrCode && qrCodeTracking),
		qrView,
		qrTileRef,
		openQrView,
		showGrid,
		openPosterView,
		getExitReason,
	};
}
